import { Link } from 'gatsby';
import { GatsbyImage, ImageDataLike, getImage, withArtDirection } from 'gatsby-plugin-image';
import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { useDataLayerPush } from '../hooks/useDataLayerPush';
import { createMarkup } from '../utils';
import { getUrlParts } from '../utils/getUrlParts';
import { Img } from './Img';
import { Panel } from './Panel';
import { Typography } from './Typography';

export interface PromoBannerProps {
  title: string;
  description?: string;
  link: any;
  onClick?: any;
  smallImage?: ImageDataLike;
  largeImage?: ImageDataLike;
  disableClickTracking?: boolean;
}

const StyledBody = styled(Panel)`
  ${({ theme: { layers } }) => css`
    position: absolute;
    bottom: 0;
    left: 0;
    height: 100%;
    width: 100%;
    z-index: ${layers.imageCardBody};
    background-image: linear-gradient(90deg, #111111 0%, rgba(17, 17, 17, 0) 100%);
  `};
`;

const StyledHeader = styled.div`
  ${({ theme: { space } }) => css`
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    max-width: 600px;
  `};
`;

const StyledDescription = styled(Typography)`
  ${({ theme: { space } }) => css`
    margin-top: ${space.sm};
  `};
`;

const StyledTitle = styled(Typography)`
  ${({ theme: { space } }) => css`
    max-width: 90%;
  `};
`;

const StyledAction = styled.div`
  ${({ theme: { space, borders, colors, media } }) => css`
    display: flex;
    align-items: center;
    gap: ${space.sm};
    padding-top: ${space.md};
    border-top: ${borders.defaultOpaque};
    color: ${colors.white.default};
    margin-top: ${space.lg};
  `};
`;

const StyledPromoBanner = styled(Link)`
  ${({ theme: { shadows, space, colors, global, layers } }) => css`
    text-decoration: none;
    text-align: left;
    border-radius: ${global.borderRadius.default};
    overflow: hidden;
    position: relative;
    color: ${colors.white.default};
    display: block;

    &:hover {
      box-shadow: ${shadows.small};

      ${StyledAction} {
        text-decoration: underline;
      }
    }

    @media (min-width: 800px) {
      .art-directed {
        width: 1120px;
        height: 280px;
      }
    }
  `};
`;

export const PromoBanner: FC<PromoBannerProps> = ({
  smallImage,
  largeImage,
  title,
  description,
  link,
  onClick,
  disableClickTracking,
  ...rest
}) => {
  const { dataLayerPush } = useDataLayerPush();

  const handleClick = (...props) => {
    if (!disableClickTracking) {
      dataLayerPush({
        event: 'image card',
        clickText: title,
        clickBladeId: undefined,
        // ...getUrlParts(url),
      });
    }

    if (onClick) {
      onClick(...props);
    }
  };

  const images = smallImage?.localFile
    ? withArtDirection(getImage(smallImage?.localFile), [
        {
          media: '(min-width: 800px)',
          image: getImage(largeImage?.localFile as ImageDataLike),
        },
      ])
    : undefined;

  return (
    <StyledPromoBanner to={link.url} target="_blank" onClick={handleClick} {...rest}>
      {images && smallImage && <GatsbyImage className="art-directed" alt="" image={images} />}
      <StyledBody>
        <StyledHeader>
          {title && (
            <StyledTitle component="h3" variant="titleMedium">
              {createMarkup(title)}
            </StyledTitle>
          )}
          {description && (
            <StyledDescription component="p" variant="bodyLarge">
              {createMarkup(description)}
            </StyledDescription>
          )}
        </StyledHeader>
        {link.name ? <StyledAction>{link.name}</StyledAction> : ''}
      </StyledBody>
    </StyledPromoBanner>
  );
};
